import React, { useEffect, useState } from "react";
import { Tab, TabList, Tabs } from "@reach/tabs";
import {
  faChevronDown,
  faChevronUp,
  faFlag,
  faRankingStar,
} from "@fortawesome/pro-solid-svg-icons";
import { ReactComponent as CalendarIcon } from "./assets/calendar.svg";
import { ReactComponent as ShieldIcon } from "./assets/shield.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import styles from "./InstantTournaments.module.scss";
import addTournament from "./assets/add-tournament.png";
import goldCoins from "./assets/gold-coins.svg";
import coins from "./assets/coins.png";
import players from "./assets/players.png";
import nock from "./assets/nock.svg";
import timeIcon from "./assets/time.png";
import { AbsoluteCenter } from "../../AbsoluteCenter";
import { loader } from "graphql.macro";
import { useQuery } from "../../../hooks/useQuery";
import {
  ScheduledTournamentsQuery,
  ScheduledTournamentsQueryVariables,
} from "../../../graphql/ScheduledTournamentsQuery.generated";
import { InstantTournament, ScheduledTournament } from "./types";
import { useTranslation } from "react-i18next";
import { Loader } from "../../Loader";
import { useMutation } from "../../../hooks/useMutation";
import {
  PurchaseScheduledTournamentEntryMutation,
  PurchaseScheduledTournamentEntryMutationVariables,
} from "./graphql/JoinScheduledTournamentMutation.generated";
import { ScheduledTournamentRounds } from "./ScheduledTournamentRounds";
import {
  ScheduledTournamentFilterPeriod,
  ScheduledTournamentSortBy,
  TableOperation,
  TournamentPlayerPerTeam,
  TournamentStatus,
  TournamentType,
} from "../../../graphql/generated";
import { useLocation, useParams } from "react-router";
import { Link, useHistory } from "react-router-dom";
import { useSubscription } from "../../../hooks/useSubscription";
import {
  ScheduledTournamentSubscription,
  ScheduledTournamentSubscriptionVariables,
} from "./graphql/ScheduledTournamentSubscription.generated";
import {
  ScheduledTournamentAuditSubscription,
  ScheduledTournamentAuditSubscriptionVariables,
} from "./graphql/ScheduledTournamentAuditSubscription.generated";
import { AddScheduledTournament } from "./AddScheduledTournament";
import dayjs from "dayjs";
import { ScheduledTournamentBoard } from "./ScheduledTournamentBoard";
import { JoinTournamentErrorModal } from "./JoinTournamentErrorModal";
import * as Sentry from "@sentry/browser";
import { useUser } from "../../UserProvider";

const SCHEDULED_TOURNAMENT_QUERY = loader(
  "../../../graphql/ScheduledTournamentsQuery.graphql"
);

const JOIN_SCHEDULED_TOURNAMENT_QUERY = loader(
  "./graphql/JoinScheduledTournamentMutation.graphql"
);

const SCHEDULED_TOURNAMENT_SUBSCRIPTION = loader(
  "./graphql/ScheduledTournamentSubscription.graphql"
);

const SCHEDULED_TOURNAMENT_ADD_SUBSCRIPTION = loader(
  "./graphql/ScheduledTournamentAuditSubscription.graphql"
);

const EmptyTournamentList = ({
  onAddTournament,
  isCreationEnabled,
}: {
  onAddTournament: (val: boolean) => void;
  isCreationEnabled: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col justify-between gap-6 h-full w-full">
      <div className="flex-1 rounded-b-lg overflow-hidden bg-gray-900 relative">
        <AbsoluteCenter>
          <div className="flex flex-col gap-3 text-white items-center text-xl leading-loose	">
            <img src={addTournament} alt="vip-icon" width="100px" />
            {t("instant_tournament.no_scheduled_tournaments")}
            {isCreationEnabled && (
              <button
                className="btn btn-blue-600 rounded-md shadow-xl text-base text-white gap-2 px-6 py-1"
                onClick={() => onAddTournament(true)}
              >
                {t("instant_tournament.create_scheduled_tournament")}
              </button>
            )}
          </div>
        </AbsoluteCenter>
      </div>
    </div>
  );
};

const TournamentItem = ({
  myTournament,
  tournament,
  onJoinTournament,

  gameId,
  roomId,
}: {
  myTournament?: boolean;
  tournament: InstantTournament | ScheduledTournament;
  onJoinTournament?: (id: string) => void;
  gameId?: string;
  roomId?: string;
}) => {
  const { t } = useTranslation();
  const { push } = useHistory();
  const [timeLeft, setTimeLeft] = useState<string>("");
  const [joinedPlayers, setJoinedPlayers] = useState(tournament.joinedPlayers);

  const [{ data: subscriptionData }] = useSubscription<
    unknown,
    ScheduledTournamentSubscription,
    ScheduledTournamentSubscriptionVariables
  >({
    query: SCHEDULED_TOURNAMENT_SUBSCRIPTION,
    variables: {
      id: tournament?.id,
    },
    throwOnError: false,
    pause: !tournament.id,
  });

  useEffect(() => {
    if (subscriptionData) {
      if (
        subscriptionData.scheduledTournament?.__typename ===
        "ScheduledTournamentEntityUpdate"
      ) {
        setJoinedPlayers(
          subscriptionData?.scheduledTournament.tournament.joinedPlayers
        );
      }
    }
  }, [subscriptionData]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = dayjs();
      const start = dayjs(tournament.startDateTime);
      const diffSeconds = start.diff(now, "second");

      if (diffSeconds <= 0) {
        setTimeLeft("");
        clearInterval(interval);
        return;
      }

      if (diffSeconds < 3600) {
        const minutes = Math.floor(diffSeconds / 60);
        setTimeLeft(`${minutes} ${t("instant_tournament.minutes")}`);
      } else {
        setTimeLeft("");
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [tournament]);

  const getMaxValue = () => {
    const awards = tournament?.awards || [];
    const maxValue = Math.max(...awards!.map((a) => a.amount.value));

    return maxValue;
  };

  return (
    <div
      className={classNames(
        "flex",
        "gap-6",
        "p-5",
        "text-white",
        "items-center",
        "rounded-lg",
        styles.tournamentItem
      )}
    >
      <div className={styles.image}>
        <img
          src={`${process.env.REACT_APP_API_HOST}/scheduled-tournaments/${tournament.image}`}
          alt="img"
        />
      </div>
      <div className="flex flex-col gap-1 flex-grow">
        <div className={styles.scheduledTime}>
          <div
            className="flex justify-center items-center"
            style={{
              width: "1.375rem",
              height: "1.375rem",
              borderRadius: "100%",
              background: "#DD6B20",
            }}
          >
            <CalendarIcon width={14} height={14} color="currentColor" />
          </div>
          <div>
            {tournament.startDateTime &&
              dayjs(tournament.startDateTime).format("ddd DD MMM · HH.mm")}
          </div>
        </div>
        <span className="font-semibold text-lg">{tournament.name}</span>
        <div className="flex gap-3 text-sm">
          <div className="flex items-center gap-2">
            <span className={styles.prize}>{t("prize")}</span>
            <img src={goldCoins} width={24} height={24} alt="coins" />
            <span className="font-semibold">{getMaxValue()}</span>
          </div>
          <div className="flex items-center gap-2">
            <img src={coins} width={16} height={16} alt="coins" />
            <span className={styles.info}>{t("instant_tournament.entry")}</span>
            <span className={styles.info}>{tournament.price.value}</span>
          </div>
          <div className="flex items-center gap-2">
            <ShieldIcon width={14} height={14} />
            <span className={styles.info}>
              {tournament.playerPerTeam === TournamentPlayerPerTeam.OneVsOne
                ? "1x1"
                : "2x2"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <img src={players} width={16} height={16} alt="coins" />
            <span className={styles.info}>{`${t("players")} ${joinedPlayers}/${
              tournament.numberOfPlayers
            }`}</span>
          </div>
          {tournament.type === TournamentType.Private && (
            <div className="flex items-center gap-2">
              <img src={nock} width={16} height={16} alt="coins" />
              <span className={styles.info}>{t("private")}</span>
            </div>
          )}
        </div>
      </div>
      {tournament.status !== TournamentStatus.Completed &&
      tournament.status !== TournamentStatus.Cancelled ? (
        <div className="w-2/12 flex flex-col gap-2">
          {!!timeLeft && (
            <div className="flex items-center gap-2">
              <img src={timeIcon} width={26} height={26} alt="" />
              <span className={classNames("text-sm", styles.info)}>
                {t("instant_tournament.starts_in")}
              </span>
              <span className="text-sm font-bold" style={{ color: "#FFAB28" }}>
                {timeLeft}
              </span>
            </div>
          )}
          {tournament.type !== TournamentType.Private &&
            (joinedPlayers !== tournament.numberOfPlayers ||
              tournament.isParticipant) && (
              <button
                className="btn-green-500 rounded-md shadow-xl text-white w-full py-1 text-sm h-7"
                onClick={() => {
                  myTournament || tournament.isParticipant
                    ? push(
                        `/area/${roomId}:${gameId}/scheduled-tournaments/current/${tournament.id}`
                      )
                    : onJoinTournament && onJoinTournament(tournament.id);
                }}
              >
                {myTournament || tournament.isParticipant
                  ? t("instant_tournament.continue")
                  : t("join")}
              </button>
            )}
        </div>
      ) : tournament.isParticipant &&
        tournament.status !== TournamentStatus.Cancelled ? (
        <button
          className="btn-orange-600 rounded-md shadow-xl text-white w-2/12 py-1 text-sm h-7"
          onClick={() => {
            push(
              `/area/${roomId}:${gameId}/scheduled-tournaments/${tournament.id}`
            );
          }}
        >
          {t("instant_tournament.view_bracket")}
        </button>
      ) : (
        <></>
      )}
    </div>
  );
};

export const ScheduledTournaments = () => {
  const { user } = useUser();
  const { sectionId, tournamentId } = useParams<{
    sectionId: string;
    tournamentId: string;
  }>();
  const { t } = useTranslation();
  const { push } = useHistory();
  const { pathname } = useLocation();
  const [scheduledTournamentList, setScheduledTournamentList] = useState<
    ScheduledTournamentsQuery | undefined
  >(undefined);
  const [activeFilterTab, setActiveFilterTab] = useState<
    ScheduledTournamentFilterPeriod
  >(ScheduledTournamentFilterPeriod.All);
  const [addScheduledTournament, setAddScheduledTournament] = useState(false);
  const [open, setOpen] = useState(false);
  const [showCreateScheduledButton, setShowCreateScheduledButton] = useState(
    false
  );
  const [showErrorJoinModal, setShowErrorJoinModal] = useState("");

  const scheduledSort = [
    {
      id: ScheduledTournamentSortBy.SoonestStart,
      label: t("instant_tournament.filter.soonest_start"),
    },
    {
      id: ScheduledTournamentSortBy.HighestPrize,
      label: t("instant_tournament.filter.highest_prize"),
    },
    {
      id: ScheduledTournamentSortBy.LowestEntryFee,
      label: t("instant_tournament.filter.lowest_entry_fee"),
    },
  ];

  const [selectedSorting, setSelectedSorting] = useState<{
    id: ScheduledTournamentSortBy;
    label: string;
  }>(scheduledSort[0]);

  const [
    tournamentStatus,
    setTournamentStatus,
  ] = useState<TournamentStatus | null>(null);

  const filterData = React.useMemo(() => {
    return {
      roomId: sectionId?.split(":")[0],
      gameId: sectionId?.split(":")[1],
    };
  }, [sectionId]);

  const now = new Date();
  const offsetMinutes = now.getTimezoneOffset();

  const [
    { data: scheduledData, fetching: scheduledFetching },
    reexecuteScheduledQuery,
  ] = useQuery<ScheduledTournamentsQuery, ScheduledTournamentsQueryVariables>({
    query: SCHEDULED_TOURNAMENT_QUERY,
    pause: !pathname?.includes("scheduled-tournaments"),
    variables: {
      filter: {
        roomId: filterData.roomId,
        gameId: filterData.gameId,
        onlyMine: false,
        onlyActive: true,
        statuses: [],
        playersPerTeam: [],
        period: {
          periodType: activeFilterTab,
          timezoneOffsetMinutes: offsetMinutes,
        },
        sortBy: selectedSorting.id,
      },
    },
  });

  useEffect(() => {
    setScheduledTournamentList(scheduledData);

    if (scheduledData?.scheduledTournaments?.isCreationEnabled) {
      setShowCreateScheduledButton(true);
    }
  }, [scheduledData, showCreateScheduledButton]);

  const fetchData = () => {
    reexecuteScheduledQuery({ requestPolicy: "network-only" });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const [{ data: subscriptionData }] = useSubscription<
    unknown,
    ScheduledTournamentSubscription,
    ScheduledTournamentSubscriptionVariables
  >({
    query: SCHEDULED_TOURNAMENT_SUBSCRIPTION,
    variables: {
      id: tournamentId,
    },
    throwOnError: false,
  });

  useEffect(() => {
    if (subscriptionData) {
      if (
        subscriptionData.scheduledTournament?.__typename ===
        "ScheduledTournamentEntityUpdate"
      ) {
        const tournament = subscriptionData?.scheduledTournament?.tournament;

        setTournamentStatus(tournament?.status!);
      }
    }
  }, [subscriptionData]);

  const [{ data: subscriptionTournaments }] = useSubscription<
    unknown,
    ScheduledTournamentAuditSubscription,
    ScheduledTournamentAuditSubscriptionVariables
  >({
    query: SCHEDULED_TOURNAMENT_ADD_SUBSCRIPTION,
    throwOnError: false,
  });

  useEffect(() => {
    if (
      subscriptionTournaments?.scheduledTournaments?.operation ===
        TableOperation.Create &&
      subscriptionTournaments?.scheduledTournaments?.tournament
    ) {
      setScheduledTournamentList({
        scheduledTournaments: {
          isCreationEnabled: !!scheduledTournamentList?.scheduledTournaments
            ?.isCreationEnabled,
          tournaments: [
            //@ts-ignore
            {
              ...subscriptionTournaments.scheduledTournaments.tournament,
              isParticipant: false,
              status: TournamentStatus.Upcoming,
            },
            ...(scheduledTournamentList?.scheduledTournaments?.tournaments ??
              []),
          ],
        },
      });
    } else if (
      subscriptionTournaments?.scheduledTournaments?.operation ===
        TableOperation.Update &&
      subscriptionTournaments?.scheduledTournaments?.tournament
    ) {
      if (
        subscriptionTournaments?.scheduledTournaments?.tournament?.status ===
        TournamentStatus.Cancelled
      ) {
        const newTournamentList = scheduledTournamentList?.scheduledTournaments?.tournaments?.filter(
          (item) =>
            item.id !==
            subscriptionTournaments?.scheduledTournaments?.tournament?.id
        );

        setScheduledTournamentList({
          scheduledTournaments: {
            isCreationEnabled: !!scheduledTournamentList?.scheduledTournaments
              ?.isCreationEnabled,
            tournaments: newTournamentList || [],
          },
        });
      }
    }
  }, [subscriptionTournaments]);

  useEffect(() => {
    if (tournamentId && scheduledTournamentList?.scheduledTournaments) {
      const currentTournament = scheduledTournamentList?.scheduledTournaments?.tournaments.find(
        (item) => item.id === tournamentId
      );

      setTournamentStatus(currentTournament?.status!);
    }
  }, [
    scheduledTournamentList,
    scheduledTournamentList?.scheduledTournaments,
    tournamentId,
  ]);

  const [, joinScheduleTournament] = useMutation<
    PurchaseScheduledTournamentEntryMutation,
    PurchaseScheduledTournamentEntryMutationVariables
  >(JOIN_SCHEDULED_TOURNAMENT_QUERY, false);

  const handleFilterTabsChange = (index: number) => {
    if (index === 0) {
      setActiveFilterTab(ScheduledTournamentFilterPeriod.Today);
    } else if (index === 1) {
      setActiveFilterTab(ScheduledTournamentFilterPeriod.Tomorrow);
    } else {
      setActiveFilterTab(ScheduledTournamentFilterPeriod.All);
    }
  };

  const handleJoinTournament = (id: string) => {
    joinScheduleTournament({
      input: {
        scheduledTournamentId: id,
        invitationId: null,
      },
    }).then((result) => {
      if (result.error) {
        setShowErrorJoinModal(result.error?.message);
        Sentry.captureMessage(
          "Error on join to scheduled tournament: scheduledTournamentId:" +
            id +
            "roomId: " +
            filterData.roomId +
            "gameId: " +
            filterData.gameId +
            "userId: " +
            user?.id
        );
        return result.error;
      } else {
        push(
          `/area/${filterData?.roomId}:${filterData.gameId}/scheduled-tournaments/current/${id}`
        );
      }
    });
  };

  const handleSort = (option: {
    id: ScheduledTournamentSortBy;
    label: string;
  }) => {
    setSelectedSorting(option);
    setOpen(!open);
  };

  return (
    <div className="h-full">
      <div className="h-full flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div
            className={classNames(
              "flex",
              "gap-3",
              "p-1",
              "rounded-md",
              styles.tabList
            )}
          >
            <Link
              to={`/area/${filterData?.roomId}:${filterData.gameId}/instant-tournaments`}
              className={classNames(
                "flex",
                "gap-3",
                "items-center",
                "py-1",
                "px-4",
                "rounded-md",
                styles.tab
              )}
            >
              <div>
                <FontAwesomeIcon icon={faRankingStar} />
              </div>
              {t("instant_tournament.instant")}
            </Link>
            <Link
              to={`/area/${filterData?.roomId}:${filterData.gameId}/scheduled-tournaments`}
              className={classNames(
                "flex",
                "gap-3",
                "items-center",
                "py-1",
                "px-4",
                "rounded-md",
                styles.tab,
                styles.active
              )}
            >
              <div className="flex items-center justify-between w-6 h-6">
                <CalendarIcon width={24} height={24} color="currentColor" />
              </div>
              {t("instant_tournament.scheduled")}
            </Link>
            <Link
              to={`/area/${filterData?.roomId}:${filterData.gameId}/my-tournaments`}
              className={classNames(
                "flex",
                "gap-3",
                "items-center",
                "py-1",
                "px-4",
                "rounded-md",
                styles.tab
              )}
            >
              <div>
                <FontAwesomeIcon icon={faFlag} />
              </div>
              {t("instant_tournament.my_tournaments")}
            </Link>
          </div>
          {showCreateScheduledButton && (
            <button
              className="btn btn-blue-600 rounded-md shadow-xl text-white gap-2 px-6 py-1"
              onClick={() => setAddScheduledTournament(true)}
            >
              {t("instant_tournament.create_scheduled_tournament")}
            </button>
          )}
        </div>
        <div className="flex items-center justify-between">
          <span className="text-white font-bold block">
            {t("instant_tournament.scheduled_tournaments")}
          </span>
          {scheduledFetching ? (
            <AbsoluteCenter>
              <Loader description={t("loading_tournament")} />
            </AbsoluteCenter>
          ) : (
            <div className="flex items-center gap-2">
              <div className={styles.scheduledFilterTabList}>
                <Tabs
                  className="flex flex-col gap-5 h-full"
                  onChange={handleFilterTabsChange}
                >
                  <TabList>
                    <Tab
                      className={classNames(styles.scheduledFilterTab, {
                        [styles.active]:
                          activeFilterTab ===
                          ScheduledTournamentFilterPeriod.Today,
                      })}
                    >
                      {t("instant_tournament.today")}
                    </Tab>
                    <Tab
                      className={classNames(styles.scheduledFilterTab, {
                        [styles.active]:
                          activeFilterTab ===
                          ScheduledTournamentFilterPeriod.Tomorrow,
                      })}
                    >
                      {t("instant_tournament.tomorrow")}
                    </Tab>
                    <Tab
                      className={classNames(styles.scheduledFilterTab, {
                        [styles.active]:
                          activeFilterTab ===
                          ScheduledTournamentFilterPeriod.All,
                      })}
                    >
                      {t("all")}
                    </Tab>
                  </TabList>
                </Tabs>
              </div>
              <div className={styles.scheduledFilterTabList}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    color: "#727587",
                    marginRight: "2px",
                    cursor: "pointer",
                    padding: "2px 16px",
                    gap: "16px",
                  }}
                  onClick={() => {
                    setOpen(!open);
                  }}
                >
                  <div>
                    {t("instant_tournament.sort")}
                    <span className="text-white pl-1">
                      {selectedSorting.label}
                    </span>
                  </div>
                  <FontAwesomeIcon
                    className="text-white"
                    icon={!open ? faChevronDown : faChevronUp}
                    size="sm"
                  />
                </div>
                {open && (
                  <div className="absolute left-0 mt-2 space-y-1 rounded-md overflow-hidden">
                    {scheduledSort.map((option, index) => (
                      <button
                        key={option.label}
                        className={classNames(
                          "w-full text-left px-4 py-2 cursor-pointer"
                        )}
                        style={{
                          background: "#1B1F28",
                          color:
                            selectedSorting.id === option.id
                              ? "#3889FF"
                              : "#ffffff",
                        }}
                        onClick={() => handleSort(option)}
                      >
                        {t(option.label)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {!!scheduledTournamentList?.scheduledTournaments?.tournaments
          ?.length ? (
          <div
            className={classNames(
              "flex",
              "flex-col",
              "flex-1",
              "gap-2",
              "rounded-lg",
              "bg-gray-900",
              "p-1",
              "overflow-auto",
              styles.scrollable
            )}
          >
            {scheduledTournamentList?.scheduledTournaments?.tournaments?.map(
              (tournament) => (
                <TournamentItem
                  key={tournament.id}
                  gameId={filterData.gameId}
                  roomId={filterData.roomId}
                  tournament={tournament}
                  onJoinTournament={handleJoinTournament}
                />
              )
            )}
          </div>
        ) : (
          <EmptyTournamentList
            onAddTournament={setAddScheduledTournament}
            isCreationEnabled={
              !!scheduledTournamentList?.scheduledTournaments?.isCreationEnabled
            }
          />
        )}
      </div>

      {addScheduledTournament && (
        <AddScheduledTournament
          section={{
            sectionId: sectionId.split(":")[0],
            tableId: sectionId.split(":")[1],
          }}
          closeHandler={() => {
            setAddScheduledTournament(false);
            fetchData();
          }}
        />
      )}
      {!!showErrorJoinModal && (
        <JoinTournamentErrorModal
          error={showErrorJoinModal}
          onClose={() => setShowErrorJoinModal("")}
        />
      )}
    </div>
  );
};
